/**
 * Jackson on the festival server deserializes annotated Date fields
 * (`startTime` / `endTime` as UTC ISO strings). Transient `startTimeMS` /
 * `endTimeMS` are not writable on the Java models, so posting MS alone leaves
 * times null / zero on the server.
 *
 * Pattern must match:
 * yyyy-MM-dd'T'HH:mm:ss.SSS'Z' (UTC)
 */

import { toEpochMs } from '@/lib/schedule-datetime';

export type ServerTimeSpan = {
  startTimeMS?: number | null;
  endTimeMS?: number | null;
  startTime?: string | null;
  endTime?: string | null;
};

export function msToServerDate(ms: number | null | undefined): string | null {
  if (ms == null || ms <= 0) {
    return null;
  }
  return new Date(ms).toISOString();
}

/**
 * Resolve a time for the server payload.
 * - Positive MS wins.
 * - `null` means explicitly cleared — do not revive from a leftover string.
 * - Otherwise fall back to ISO-only date strings (never table display text).
 */
function resolveMsForServer(
  ms: number | null | undefined,
  dateValue: string | number | Date | null | undefined,
): number | null {
  if (typeof ms === 'number' && ms > 0) {
    return ms;
  }
  if (ms === null) {
    return null;
  }
  return toEpochMs(undefined, dateValue);
}

/**
 * Normalize MS (from MS or ISO) and set startTime/endTime for the server.
 * Clears Date fields when no usable time remains so a stale display/ISO
 * string cannot be posted after the user clears a time.
 */
export function withServerDateFields<T>(timeSpan: T): T {
  if (!timeSpan || typeof timeSpan !== 'object') {
    return timeSpan;
  }
  const source = timeSpan as T & ServerTimeSpan;
  const next = { ...source };
  const startMs = resolveMsForServer(next.startTimeMS, next.startTime);
  const endMs = resolveMsForServer(next.endTimeMS, next.endTime);

  if (startMs != null) {
    next.startTimeMS = startMs;
    next.startTime = msToServerDate(startMs);
  } else {
    next.startTimeMS = null;
    next.startTime = null;
  }

  if (endMs != null) {
    next.endTimeMS = endMs;
    next.endTime = msToServerDate(endMs);
  } else {
    next.endTimeMS = null;
    next.endTime = null;
  }

  return next;
}

export function withServerDateFieldsList<T>(list: T): T {
  if (!Array.isArray(list)) {
    return list;
  }
  return list.map(withServerDateFields) as T;
}
